import { Unit } from "@/types/unit";
import { StatType } from "@/types/stats";

// See https://github.com/d3/d3-format
export const TICK_FORMATS = {
  percentage: ".0%", // multiplies by 100, adds % sign
  comma: ",", // comma separate thousands
  si: "~s",
};

export const CHART_CONFIG: { [propName: string]: any } = {
  // DEBUG MODE - SLAB STATS
  slabSize: {
    title: "Slab Sizes",
    statType: StatType.SLAB,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: [
          "bytes_64",
          "bytes_128",
          "bytes_256",
          "bytes_512",
          "bytes_1024",
          "bytes_2048",
          "bytes_4096",
          "bytes_8129",
          "bytes_16384",
          "bytes_32768",
          "bytes_65536",
          "bytes_131072",
          "bytes_262144",
          "bytes_524288",
          "bytes_1048576",
        ],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  slabItems: {
    title: "Slab Items",
    statType: StatType.SLAB,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: [
          "items_64",
          "items_128",
          "items_256",
          "items_512",
          "items_1024",
          "items_2048",
          "items_4096",
          "items_8129",
          "items_16384",
          "items_32768",
          "items_65536",
          "items_131072",
          "items_262144",
          "items_524288",
          "items_1048576",
        ],
        format: TICK_FORMATS.si,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  slabEvictionRate: {
    title: "Slab Eviction Rate",
    statType: StatType.SLAB,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: [
          "eviction_rate_64",
          "eviction_rate_128",
          "eviction_rate_256",
          "eviction_rate_512",
          "eviction_rate_1024",
          "eviction_rate_2048",
          "eviction_rate_4096",
          "eviction_rate_8129",
          "eviction_rate_16384",
          "eviction_rate_32768",
          "eviction_rate_65536",
          "eviction_rate_131072",
          "eviction_rate_262144",
          "eviction_rate_524288",
          "eviction_rate_1048576",
        ],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  slabEvictionUnexpiredRate: {
    title: "Slab Eviction Unexpired Rate",
    statType: StatType.SLAB,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: [
          "eviction_unexpired_rate_64",
          "eviction_unexpired_rate_128",
          "eviction_unexpired_rate_256",
          "eviction_unexpired_rate_512",
          "eviction_unexpired_rate_1024",
          "eviction_unexpired_rate_2048",
          "eviction_unexpired_rate_4096",
          "eviction_unexpired_rate_8129",
          "eviction_unexpired_rate_16384",
          "eviction_unexpired_rate_32768",
          "eviction_unexpired_rate_65536",
          "eviction_unexpired_rate_131072",
          "eviction_unexpired_rate_262144",
          "eviction_unexpired_rate_524288",
          "eviction_unexpired_rate_1048576",
        ],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  slabOutofmemory: {
    title: "Slab Out of Memory",
    statType: StatType.SLAB,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: [
          "outofmemory_64",
          "outofmemory_128",
          "outofmemory_256",
          "outofmemory_512",
          "outofmemory_1024",
          "outofmemory_2048",
          "outofmemory_4096",
          "outofmemory_8129",
          "outofmemory_16384",
          "outofmemory_32768",
          "outofmemory_65536",
          "outofmemory_131072",
          "outofmemory_262144",
          "outofmemory_524288",
          "outofmemory_1048576",
        ],
        format: TICK_FORMATS.si,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  // DEBUG MODE - PREFIX STATS
  prefixSize: {
    title: "Recently Touched Memory",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_total_size", "compound_total_size"],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixSizePercent: {
    title: "Fraction of Recently Touched Memory",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_over_compound_total_size"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixHitRate: {
    title: "Hit Rate",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_hit_rate", "hit_rate"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixHitsMissesMutationsRate: {
    title: "Prefix Activity",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_total_hits_rate", "prefix_total_misses_rate", "prefix_total_mutations_rate"],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixHitsMissesMutations: {
    title: "Prefix Request Fraction",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: [
          "prefix_over_compound_total_hits_rate",
          "prefix_over_compound_total_misses_rate",
          "prefix_over_compound_total_mutations_rate",
        ],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixNumKeys: {
    title: "Recently Touched Items",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_num_keys", "compound_num_keys"],
        format: TICK_FORMATS.si,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixNumKeysPercent: {
    title: "Fraction of Recently Touched Items",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_over_compound_num_keys"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  prefixRelativeActivity: {
    title: "Prefix Weight",
    statType: StatType.PREFIX,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["prefix_over_compound_requests", "prefix_over_compound_num_keys", "prefix_over_compound_total_size"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  // DEBUG MODE - COMPOUND STATS
  compoundSizeAbsolute: {
    title: "Compound Size",
    statType: StatType.COMPOUND,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["compound_total_size", "bytes"],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  compoundNumKeys: {
    title: "Compound num_keys",
    statType: StatType.COMPOUND,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["compound_num_keys", "items"],
        format: TICK_FORMATS.si,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  keyHitsMissesMutations: {
    title: "Key Activity",
    statType: StatType.KEY,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["key_mutation_count_per_sec", "key_hit_count_per_sec", "key_miss_count_per_sec"],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  // ANALYTICS
  memoryAndHitRate: {
    title: "Memory & Hit Rate",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "Memory",
        metrics: ["bytes", "max_bytes"],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES,
      },
      right: {
        label: "Hit Rate",
        metrics: ["hit_rate"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
    },
  },
  requestRate: {
    title: "Request Rate",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: [
          "request_rate",
          "get_rate",
          "set_rate",
          "delete_rate",
          "incr_rate",
          "decr_rate",
          "touch_rate",
          "flush_rate",
        ],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  requestDistribution: {
    title: "Request Distribution",
    statType: StatType.PROCESSED,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: [
          "get_fraction",
          "set_fraction",
          "delete_fraction",
          "incr_fraction",
          "decr_fraction",
          "touch_fraction",
          "flush_fraction",
        ],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  hitRate: {
    title: "Hit Rate",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["hit_rate", "get_hit_rate", "delete_hit_rate", "incr_hit_rate", "decr_hit_rate", "touch_hit_rate"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  items: {
    title: "Stored Items",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["items"],
        format: TICK_FORMATS.si,
        unit: "",
      },
      right: {
        label: "",
        metrics: ["eviction_rate", "get_expired_rate"],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
    },
  },
  dataTraffic: {
    title: "Data Traffic",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["bytes_read_rate", "bytes_write_rate"],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES_PER_SECOND,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  connections: {
    title: "Connections",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["connections"],
        format: TICK_FORMATS.comma,
        unit: "",
      },
      right: {
        label: "",
        metrics: ["connection_rate"],
        format: TICK_FORMATS.comma,
        unit: Unit.PER_SECOND,
      },
    },
  },
  memory: {
    title: "Memory",
    statType: StatType.PROCESSED,
    stacked: false,
    axes: {
      left: {
        label: "",
        metrics: ["bytes", "max_bytes"],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  watcherStats: {
    title: "Watcher Stats",
    statType: StatType.WATCHER,
    stacked: false,
    axes: {
      left: {
        label: "# Logs",
        metrics: ["skipped", "dropped", "processed"],
        format: TICK_FORMATS.si,
        unit: "",
      },
      right: {
        label: "Drift",
        metrics: ["drift", "restarts"],
        format: TICK_FORMATS.si,
        unit: "",
      },
    },
  },
  watcherStatsFractions: {
    title: "Watcher Stats Fractions",
    statType: StatType.WATCHER,
    stacked: true,
    axes: {
      left: {
        label: "",
        metrics: ["skipped_fraction", "dropped_fraction", "processed_fraction"],
        format: TICK_FORMATS.percentage,
        unit: "",
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  cpuIdle: {
    title: "CPU Idle",
    statType: StatType.RAW_INFLUXDB,
    stacked: false,
    axes: {
      left: {
        label: "CPU Idle",
        metrics: ["idle", "user", "system", "softirq", "steal", "nice", "irq", "iowait", "guest", "guest_nice"],
        format: TICK_FORMATS.si, // values returned as % of 100, not of 1, so do not use TICK_FORMATS.percentage, which x100's values.
        unit: Unit.PERCENT,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
  memFree: {
    title: "Free Memory",
    statType: StatType.RAW_INFLUXDB,
    stacked: false,
    axes: {
      left: {
        label: "Free Memory",
        metrics: ["total", "free"],
        format: TICK_FORMATS.si,
        unit: Unit.BYTES,
      },
      right: {
        label: "",
        metrics: [],
        format: "",
        unit: "",
      },
    },
  },
};
